import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { MapPin, Loader2, Navigation, Bed, Bath, Maximize, AlertTriangle } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * "Find properties near me" button.
 *
 * Asks the browser for geolocation, then queries `/api/properties/nearby`
 * and shows the results in a modal sorted by distance from the user. Each
 * row links to the detail page. Geolocation refusal / unavailable is
 * surfaced with a toast and the dialog stays closed.
 */
export default function NearMeButton({ variant = 'hero', defaultRadiusKm = 25 }) {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const tt = (en, es) => (language === 'es' ? es : en);

  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState([]);
  const [error, setError] = useState('');
  const [radiusKm, setRadiusKm] = useState(defaultRadiusKm);
  const [origin, setOrigin] = useState(null); // {lat, lng}

  const fetchNearby = async (lat, lng, radius = radiusKm) => {
    try {
      const r = await axios.get(`${API}/properties/nearby`, {
        params: { lat, lng, radius_km: radius, limit: 30 },
      });
      setResults(r.data?.properties || []);
      setError('');
    } catch (err) {
      setError(err?.response?.data?.detail || tt('Unable to load nearby properties', 'No se pudieron cargar las propiedades cercanas'));
    }
  };

  const start = () => {
    if (!('geolocation' in navigator)) {
      toast.error(tt(
        'Geolocation is not supported by your browser',
        'Tu navegador no soporta geolocalización'
      ));
      return;
    }
    setLoading(true);
    setError('');
    setOpen(true);
    setResults([]);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { latitude, longitude } = pos.coords;
        setOrigin({ lat: latitude, lng: longitude });
        await fetchNearby(latitude, longitude, radiusKm);
        setLoading(false);
      },
      (err) => {
        setLoading(false);
        let msg = tt('Could not get your location', 'No pudimos obtener tu ubicación');
        if (err.code === 1) msg = tt('Location permission denied', 'Permiso de ubicación denegado');
        if (err.code === 2) msg = tt('Position unavailable', 'Posición no disponible');
        if (err.code === 3) msg = tt('Location request timed out', 'Tiempo agotado al pedir ubicación');
        setError(msg);
        toast.error(msg);
      },
      { enableHighAccuracy: false, timeout: 10000, maximumAge: 60_000 }
    );
  };

  const changeRadius = async (r) => {
    setRadiusKm(r);
    if (origin) {
      setLoading(true);
      await fetchNearby(origin.lat, origin.lng, r);
      setLoading(false);
    }
  };

  const formatPrice = (p, currency = 'USD', op = 'sale') => {
    if (!p) return '—';
    try {
      return new Intl.NumberFormat(language === 'es' ? 'es-VE' : 'en-US', {
        style: 'currency', currency, maximumFractionDigits: 0,
      }).format(p) + (op === 'rent' ? '/mo' : '');
    } catch { return `$${p}`; }
  };

  const triggerClasses = variant === 'hero'
    ? 'h-12 px-4 bg-white/10 hover:bg-white/15 backdrop-blur-md border border-white/20 hover:border-[#D4AF37]/50 text-white gap-2 rounded-xl font-medium transition'
    : 'h-10 px-4 bg-[#0B192C] hover:bg-[#0B192C]/90 text-white gap-2';

  return (
    <>
      <Button onClick={start} className={triggerClasses} data-testid="near-me-button">
        <Navigation className="w-4 h-4 text-[#D4AF37]" />
        {tt('Properties near me', 'Propiedades cerca de mí')}
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="near-me-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Navigation className="w-5 h-5 text-[#D4AF37]" />
              {tt('Properties near me', 'Propiedades cerca de mí')}
            </DialogTitle>
            <DialogDescription className="text-xs text-slate-500">
              {tt(
                'Approved listings sorted by distance from your current location.',
                'Propiedades aprobadas ordenadas por distancia desde tu ubicación.'
              )}
            </DialogDescription>
          </DialogHeader>

          {/* Radius selector */}
          {origin && (
            <div className="flex flex-wrap items-center gap-2 pt-1">
              <span className="text-xs text-slate-500">{tt('Radius:', 'Radio:')}</span>
              {[5, 10, 25, 50, 100].map(r => (
                <button
                  key={r}
                  onClick={() => changeRadius(r)}
                  className={`px-2.5 py-1 rounded-full text-xs font-medium transition ${
                    radiusKm === r
                      ? 'bg-[#D4AF37] text-[#0B192C]'
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                  data-testid={`near-me-radius-${r}`}
                >
                  {r} km
                </button>
              ))}
            </div>
          )}

          <div className="flex-1 overflow-y-auto -mx-2 px-2 mt-2" data-testid="near-me-results">
            {loading && (
              <div className="flex flex-col items-center justify-center py-10 text-slate-500" data-testid="near-me-loading">
                <Loader2 className="w-6 h-6 animate-spin mb-2 text-[#D4AF37]" />
                <p className="text-sm">{tt('Locating you & finding properties...', 'Ubicándote y buscando propiedades...')}</p>
              </div>
            )}
            {!loading && error && (
              <div className="flex flex-col items-center justify-center py-10 text-slate-500" data-testid="near-me-error">
                <AlertTriangle className="w-6 h-6 mb-2 text-amber-500" />
                <p className="text-sm">{error}</p>
              </div>
            )}
            {!loading && !error && results.length === 0 && origin && (
              <div className="text-center py-10 text-slate-500" data-testid="near-me-empty">
                <p className="text-sm">{tt(
                  `No approved listings within ${radiusKm} km — try a wider radius.`,
                  `Sin propiedades aprobadas en ${radiusKm} km — intenta con un radio mayor.`
                )}</p>
              </div>
            )}
            {!loading && !error && results.length > 0 && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {results.map(p => (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => { setOpen(false); navigate(`/property/${p.id}`); }}
                    className="text-left bg-white border border-slate-200 hover:border-[#D4AF37] rounded-xl overflow-hidden hover:shadow-md transition group"
                    data-testid={`near-me-result-${p.id}`}
                  >
                    {p.main_photo ? (
                      <div className="h-28 bg-slate-100 overflow-hidden">
                        <img src={p.main_photo} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition" />
                      </div>
                    ) : (
                      <div className="h-28 bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center">
                        <MapPin className="w-6 h-6 text-slate-400" />
                      </div>
                    )}
                    <div className="p-3">
                      <div className="flex items-start justify-between gap-2 mb-1">
                        <span className="font-medium text-sm line-clamp-1">{p.title}</span>
                        <span className="shrink-0 text-[10px] font-mono bg-[#D4AF37]/10 text-[#0B192C] border border-[#D4AF37]/40 px-1.5 py-0.5 rounded-full">
                          {p.distance_km < 1 ? `${Math.round(p.distance_km * 1000)} m` : `${p.distance_km.toFixed(1)} km`}
                        </span>
                      </div>
                      <div className="text-xs text-slate-500 line-clamp-1">{p.city}{p.neighborhood ? `, ${p.neighborhood}` : ''}</div>
                      <div className="flex items-center justify-between mt-2">
                        <span className="font-heading font-semibold text-sm text-[#0B192C]">{formatPrice(p.price, p.currency, p.operation_type)}</span>
                        <div className="flex items-center gap-2 text-[10px] text-slate-500">
                          {p.bedrooms > 0 && <span className="inline-flex items-center gap-0.5"><Bed className="w-3 h-3" />{p.bedrooms}</span>}
                          {p.bathrooms > 0 && <span className="inline-flex items-center gap-0.5"><Bath className="w-3 h-3" />{p.bathrooms}</span>}
                          {p.area > 0 && <span className="inline-flex items-center gap-0.5"><Maximize className="w-3 h-3" />{p.area}m²</span>}
                        </div>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
